// Content for the SEO landing pages. All copy is bilingual (en/it) and rendered
// according to the site language toggle. FAQ text is reused verbatim for the
// FAQPage JSON-LD, so structured data always matches the visible page.

export const SEO_PAGES = {
  "jewelry-casting-italy": {
    path: "/jewelry-casting-italy/",
    meta: {
      en: {
        title: "Jewellery Casting in Italy | DA Creations Atelier",
        description:
          "Jewellery casting in Italy for designers and brands: gold, silver, platinum and palladium cast in Urbino and priced at live market spot rates. Request a quote.",
      },
      it: {
        title: "Fusione di Gioielli in Italia | DA Creations Atelier",
        description:
          "Fusione di gioielli in Italia per designer e brand: oro, argento, platino e palladio fusi a Urbino, alla quotazione live di mercato. Richiedi un preventivo.",
      },
    },
    eyebrow: { en: "Jewellery Casting — Urbino, Italy", it: "Fusione di Gioielli — Urbino, Italia" },
    h1: { en: "Jewellery casting in Italy, at live market rates", it: "Fusione di gioielli in Italia, alla quotazione live" },
    lead: {
      en: "DA Creations Atelier is an artisan casting lab in Urbino, in the Marche region of Central Italy. We cast for independent designers, jewellery brands and retail jewellers worldwide — and every quote prices your metal against the live market spot rate, so you always know exactly what you are paying for.",
      it: "DA Creations Atelier è un laboratorio artigianale di fusione a Urbino, nelle Marche, nel cuore dell'Italia. Fondiamo per designer indipendenti, brand di gioielleria e rivenditori in tutto il mondo — e ogni preventivo valorizza il metallo alla quotazione live di mercato, così sai sempre esattamente cosa stai pagando.",
    },
    sections: [
      {
        h2: { en: "Metals we cast", it: "I metalli che fondiamo" },
        paras: [
          {
            en: "From a single prototype to a full production run, every alloy is prepared in-house and poured with vacuum-assisted casting for dense, clean results.",
            it: "Dal singolo prototipo alla tiratura completa, ogni lega è preparata internamente e colata con fusione assistita da vuoto, per risultati densi e puliti.",
          },
        ],
        list: [
          { en: "Sterling silver 925", it: "Argento 925" },
          { en: "Gold 9k, 18k and 24k — yellow, rose and white", it: "Oro 9k, 18k e 24k — giallo, rosa e bianco" },
          { en: "Platinum and palladium", it: "Platino e palladio" },
          { en: "Brass and bronze for samples and fashion pieces", it: "Ottone e bronzo per campionari e bijoux" },
          { en: "100% recycled alloys on request", it: "Leghe 100% riciclate su richiesta" },
        ],
      },
      {
        h2: { en: "From your model to finished casting", it: "Dal modello al pezzo fuso" },
        paras: [
          {
            en: "Send us a hand-carved wax, a printed prototype or a CAD file — we take it from there. Casting, gemstone setting, engraving and hand finishing all happen under one roof, with multi-stage quality control between each step.",
            it: "Inviaci una cera scolpita a mano, un prototipo stampato o un file CAD — al resto pensiamo noi. Fusione, incastonatura, incisione e finitura a mano avvengono sotto lo stesso tetto, con controlli qualità in più fasi tra un passaggio e l'altro.",
          },
        ],
      },
      {
        h2: { en: "Transparent, market-linked pricing", it: "Prezzi trasparenti, legati al mercato" },
        paras: [
          {
            en: "Metal is invoiced at the live market spot rate on the day of your order — never at an opaque fixed markup. Our online estimator shows the metal value and the craftsmanship separately, so you can test alloys, weights and quantities before you ever write to us.",
            it: "Il metallo è fatturato alla quotazione live di mercato del giorno dell'ordine — mai con un ricarico fisso opaco. Il nostro preventivatore online mostra il valore del metallo e la lavorazione separatamente: puoi provare leghe, pesi e quantità prima ancora di scriverci.",
          },
        ],
      },
    ],
    faq: [
      {
        q: { en: "Which metals can you cast?", it: "Quali metalli potete fondere?" },
        a: {
          en: "We cast sterling silver 925, gold in 9k, 18k and 24k alloys, platinum and palladium, plus brass or bronze for samples and fashion lines. Recycled alloys are available on request.",
          it: "Fondiamo argento 925, oro in leghe 9k, 18k e 24k, platino e palladio, oltre a ottone e bronzo per campionari e linee moda. Leghe riciclate disponibili su richiesta.",
        },
      },
      {
        q: { en: "How is the metal priced?", it: "Come viene calcolato il prezzo del metallo?" },
        a: {
          en: "Metal is invoiced at the live market spot rate on the day of your order, shown transparently in our online estimator. Craftsmanship is quoted separately, so you can always see both parts of the cost.",
          it: "Il metallo è fatturato alla quotazione live di mercato del giorno dell'ordine, visibile in modo trasparente nel nostro preventivatore online. La manodopera è quotata separatamente, così vedi sempre entrambe le voci di costo.",
        },
      },
      {
        q: { en: "Do you work with clients outside Italy?", it: "Lavorate con clienti fuori dall'Italia?" },
        a: {
          en: "Yes. We regularly work with designers, brands and retailers worldwide, in English and Italian, from single prototypes to production runs.",
          it: "Sì. Lavoriamo regolarmente con designer, brand e rivenditori in tutto il mondo, in italiano e in inglese, dal singolo prototipo alla produzione.",
        },
      },
      {
        q: { en: "What do you need to prepare a quote?", it: "Cosa vi serve per preparare un preventivo?" },
        a: {
          en: "A sketch, photo or CAD file, the type of piece, your preferred metal and an approximate quantity are enough to start. You can attach files directly in the project brief form.",
          it: "Per iniziare bastano un bozzetto, una foto o un file CAD, il tipo di pezzo, il metallo preferito e una quantità approssimativa. Puoi allegare i file direttamente nel modulo del brief.",
        },
      },
    ],
    related: ["lost-wax-casting-jewelry", "private-label-jewelry-italy"],
  },

  "lost-wax-casting-jewelry": {
    path: "/lost-wax-casting-jewelry/",
    meta: {
      en: {
        title: "Lost-Wax Casting for Jewellery | DA Creations Atelier",
        description:
          "Lost-wax casting for jewellery, from hand-carved or 3D-printed wax models to vacuum-cast gold and silver, finished by hand in our atelier in Urbino, Italy.",
      },
      it: {
        title: "Fusione a Cera Persa per Gioielli | DA Creations Atelier",
        description:
          "Fusione a cera persa per gioielli: da modelli in cera scolpiti a mano o stampati in 3D a oro e argento fusi sotto vuoto, rifiniti a mano a Urbino.",
      },
    },
    eyebrow: { en: "The Craft — Lost-Wax Casting", it: "Il Mestiere — Fusione a Cera Persa" },
    h1: { en: "Lost-wax casting for jewellery", it: "Fusione a cera persa per gioielli" },
    lead: {
      en: "Lost-wax casting is the oldest way to turn an idea into precious metal — and still the finest. In our Urbino atelier we pair the ancient technique with vacuum-assisted pouring and microscope-level quality control, so every casting carries the exact detail of its model.",
      it: "La fusione a cera persa è il modo più antico di trasformare un'idea in metallo prezioso — ed è ancora il migliore. Nel nostro atelier di Urbino uniamo la tecnica antica alla colata assistita da vuoto e a controlli qualità al microscopio: ogni fusione conserva il dettaglio esatto del suo modello.",
    },
    sections: [
      {
        h2: { en: "What is lost-wax casting?", it: "Cos'è la fusione a cera persa?" },
        paras: [
          {
            en: "Your design is first made as a wax model. The model is sealed in plaster, the wax is burned away, and molten metal is poured into the perfect cavity it leaves behind. The wax disappears — the metal takes its exact shape. That is why the quality of the model decides the quality of the piece.",
            it: "Il tuo design nasce come modello in cera. Il modello viene racchiuso nel gesso, la cera viene eliminata dal calore e il metallo fuso ne occupa la cavità perfetta. La cera sparisce — il metallo ne prende la forma esatta. Per questo la qualità del modello decide la qualità del pezzo.",
          },
        ],
      },
      {
        h2: { en: "Our process, step by step", it: "Il nostro processo, passo dopo passo" },
        list: [
          { en: "Wax model — hand-carved by our artisans or 3D-printed at 16-micron resolution", it: "Modello in cera — scolpito a mano dai nostri artigiani o stampato in 3D a 16 micron" },
          { en: "Investment — the model is sealed in a plaster mould", it: "Rivestimento — il modello viene racchiuso in uno stampo di gesso" },
          { en: "Burnout — the wax leaves a perfect cavity", it: "Sceratura — la cera lascia una cavità perfetta" },
          { en: "Vacuum-assisted pour — dense, porosity-free castings", it: "Colata assistita da vuoto — fusioni dense e prive di porosità" },
          { en: "Multi-stage quality control between every step", it: "Controllo qualità in più fasi tra ogni passaggio" },
          { en: "Hand finishing at the bench — ready for setting and hallmarking", it: "Finitura a mano al banco — pronto per incastonatura e punzonatura" },
        ],
      },
      {
        h2: { en: "Hand-carved wax or 3D-printed prototype", it: "Cera scolpita a mano o prototipo 3D" },
        paras: [
          {
            en: "Choose the path your design needs. Our wax carvers sculpt one-of-a-kind organic forms that no machine can imitate; our CAD team turns sketches into manufacture-ready 3DM files with photoreal renders and a 16-micron printed wax you can hold, try and approve before a single gram is cast.",
            it: "Scegli il percorso che serve al tuo design. I nostri scultori modellano cere uniche e organiche che nessuna macchina sa imitare; il nostro team CAD trasforma i bozzetti in file 3DM pronti per la produzione, con render fotorealistici e una cera stampata a 16 micron da toccare, provare e approvare prima di fondere un solo grammo.",
          },
        ],
      },
    ],
    faq: [
      {
        q: { en: "Can you cast from my own wax model?", it: "Potete fondere dal mio modello in cera?" },
        a: {
          en: "Yes — send us your hand-carved or printed wax and we cast it as supplied. We inspect every model first and flag anything that could affect the casting.",
          it: "Sì — inviaci la tua cera scolpita a mano o stampata e la fondiamo così com'è. Esaminiamo prima ogni modello e segnaliamo tutto ciò che potrebbe influire sulla fusione.",
        },
      },
      {
        q: { en: "Can you cast from a CAD file instead?", it: "Potete fondere da un file CAD?" },
        a: {
          en: "Yes. We accept 3DM and STL files, print a 16-micron wax prototype for your approval, and cast once you sign it off.",
          it: "Sì. Accettiamo file 3DM e STL, stampiamo un prototipo in cera a 16 micron per la tua approvazione e fondiamo solo dopo il tuo ok.",
        },
      },
      {
        q: { en: "Which alloys work with lost-wax casting?", it: "Quali leghe si possono fondere a cera persa?" },
        a: {
          en: "Silver 925, gold alloys from 9k to 24k, platinum, palladium, and brass or bronze. We are happy to advise on the right alloy for your design.",
          it: "Argento 925, leghe d'oro dal 9k al 24k, platino, palladio, ottone e bronzo. Siamo felici di consigliarti la lega giusta per il tuo design.",
        },
      },
      {
        q: { en: "Is lost-wax casting suitable for a single piece?", it: "La cera persa è adatta a un pezzo unico?" },
        a: {
          en: "Absolutely — it is the traditional method for one-of-a-kind pieces, and it scales equally well to small production runs.",
          it: "Assolutamente — è il metodo tradizionale per i pezzi unici e si adatta altrettanto bene alle piccole tirature.",
        },
      },
    ],
    related: ["jewelry-casting-italy", "private-label-jewelry-italy"],
  },

  "private-label-jewelry-italy": {
    path: "/private-label-jewelry-italy/",
    meta: {
      en: {
        title: "Private Label Jewellery Made in Italy | DA Creations",
        description:
          "Private label jewellery manufacturing in Italy: confidential production for brands and designers, from prototype to full runs, all under one roof in Urbino.",
      },
      it: {
        title: "Gioielli Private Label Made in Italy | DA Creations",
        description:
          "Produzione di gioielli private label in Italia: produzione riservata per brand e designer, dal prototipo alle tirature, tutto sotto un unico tetto a Urbino.",
      },
    },
    eyebrow: { en: "Private Label — Made in Italy", it: "Private Label — Made in Italy" },
    h1: { en: "Private label jewellery manufacturing in Italy", it: "Produzione di gioielli private label in Italia" },
    lead: {
      en: "Your brand, our bench. DA Creations Atelier manufactures collections for jewellery and fashion brands, independent designers and retail jewellers who want genuine Italian craftsmanship behind their label — confidentially, from first prototype to full production.",
      it: "Il tuo brand, il nostro banco. DA Creations Atelier produce collezioni per brand di gioielleria e moda, designer indipendenti e rivenditori che vogliono il vero artigianato italiano dietro la propria etichetta — in totale riservatezza, dal primo prototipo alla produzione.",
    },
    sections: [
      {
        h2: { en: "Your brand, our bench", it: "Il tuo brand, il nostro banco" },
        paras: [
          {
            en: "We work as partners, not just producers. Bring us finished CAD files, rough sketches or a moodboard — we listen to what a piece needs to be, and return something that feels like it was always meant to exist. Every piece leaves the atelier ready to carry your name.",
            it: "Lavoriamo come partner, non solo come produttori. Portaci file CAD finiti, bozzetti o una moodboard — ascoltiamo cosa deve essere un pezzo e restituiamo qualcosa che sembra sempre dover esistere. Ogni pezzo lascia l'atelier pronto a portare il tuo nome.",
          },
        ],
      },
      {
        h2: { en: "From prototype to production", it: "Dal prototipo alla produzione" },
        list: [
          { en: "CAD modelling with photoreal renders and spec sheets", it: "Progettazione CAD con render fotorealistici e schede tecniche" },
          { en: "16-micron wax prototype to hold, try and approve", it: "Prototipo in cera a 16 micron da toccare, provare e approvare" },
          { en: "Casting, gemstone setting, engraving and finishing under one roof", it: "Fusione, incastonatura, incisione e finitura sotto lo stesso tetto" },
          { en: "Small runs for new collections, larger runs for established lines", it: "Piccole tirature per nuove collezioni, produzioni più ampie per linee consolidate" },
        ],
      },
      {
        h2: { en: "Confidential by default", it: "Riservatezza garantita" },
        paras: [
          {
            en: "Every brief is treated in strict confidence and client project details are never shared with third parties. Your designs, your suppliers and your launch plans stay yours — we never showcase private-label work without your written permission.",
            it: "Ogni brief è trattato con la massima riservatezza e i dettagli dei progetti non vengono mai condivisi con terzi. I tuoi design, i tuoi fornitori e i tuoi piani di lancio restano tuoi — non mostriamo mai lavori private label senza il tuo permesso scritto.",
          },
        ],
      },
    ],
    faq: [
      {
        q: { en: "Who do you work with?", it: "Con chi lavorate?" },
        a: {
          en: "Independent designers, jewellery and fashion brands, and retail jewellers — from first collections to established labels.",
          it: "Designer indipendenti, brand di gioielleria e moda, e rivenditori — dalle prime collezioni ai marchi affermati.",
        },
      },
      {
        q: { en: "Will my designs stay confidential?", it: "I miei design resteranno riservati?" },
        a: {
          en: "Yes. Every brief is treated in strict confidence and client project details are never shared with third parties.",
          it: "Sì. Ogni brief è trattato con la massima riservatezza e i dettagli dei progetti non vengono mai condivisi con terzi.",
        },
      },
      {
        q: { en: "Can you produce from my CAD files?", it: "Potete produrre dai miei file CAD?" },
        a: {
          en: "Yes, we accept manufacture-ready 3DM and STL files — and if you only have a sketch, our CAD team can model it for you.",
          it: "Sì, accettiamo file 3DM e STL pronti per la produzione — e se hai solo un bozzetto, il nostro team CAD può modellarlo per te.",
        },
      },
      {
        q: { en: "Can I approve a prototype before production?", it: "Posso approvare un prototipo prima della produzione?" },
        a: {
          en: "Always. You receive a physical wax prototype — and photoreal renders where useful — to hold, try and approve before any metal is cast.",
          it: "Sempre. Ricevi un prototipo fisico in cera — e render fotorealistici quando utili — da toccare, provare e approvare prima che venga fuso qualsiasi metallo.",
        },
      },
    ],
    related: ["jewelry-casting-italy", "lost-wax-casting-jewelry"],
  },
};

export const SEO_SLUGS = Object.keys(SEO_PAGES);
